// ─────────────────────────────────────────────────────────────────
// Glass-i — TDS / TCS API client (Stage 6P.9).
//
// Thin axios wrappers around:
//   /api/erp/tds-tcs/*  (TDS / TCS Deduction + Challan +
//                        Section Rules + Supplier Tax Profiles —
//                        Stage 6P.9)
//
// Mirrors the purchaseReturnApi / supplierPaymentApi shape — the
// `unwrap` helper handles both the {data:{data:...}} envelope used by
// most list / create endpoints and the bare {data:...} shape used by
// preview / status / helper routes.
//
// Backend is the source of truth for ALL TDS / TCS math + lifecycle:
//   - deduction_no / challan_no allocation (TDS-/TCS-/TDSCH-YYYYMMDD-NNNNNN)
//     via advisory locks,
//   - tds_tcs_rules row selection by tax_type + section_code + date
//     window (effective_from <= date <= effective_to),
//   - PAN-aware rate ladder (higher_rate_no_pan_percent kicks in when
//     supplier PAN is missing/unavailable),
//   - lower-deduction certificate amount-used tracking,
//   - threshold register (PER_SUPPLIER_FY cumulative_base_amount vs
//     threshold_amount; THRESHOLD_NOT_CROSSED suppresses preview),
//   - voucher posting via the shared voucherPosting public contract
//     (ledger fallback chain: '<SECTION>-TDS-PAYABLE' → 'TDS-PAYABLE'
//      → 'STATUTORY-PAYABLE' → 'SUNDRY-CREDITORS-TDS'),
//   - reversal on Purchase Return (register-only, no voucher cancel),
//   - challan paid lifecycle (PAID flips deductions.challan_status to
//     PAID + posts the TDS challan payment voucher).
// Frontend mirrors for instant UX but never wins.
// ─────────────────────────────────────────────────────────────────

import client from '../../../api/client';

const TT_BASE = '/api/erp/tds-tcs';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

export const tdsTcsApi = {
  // ── Section rules ────────────────────────────────────────────
  // CRUD over tds_tcs_rules. The backend keeps these as the single
  // source for rate / threshold / timing — service code MUST NOT
  // hardcode rates.
  listRules:   (params = {}) =>
    client.get(`${TT_BASE}/rules`, { params }).then(unwrap),
  getRule:     (id) =>
    client.get(`${TT_BASE}/rules/${encodeURIComponent(id)}`).then(unwrap),
  createRule:  (payload = {}) =>
    client.post(`${TT_BASE}/rules`, payload).then(unwrap),
  updateRule:  (id, payload = {}) =>
    client.put(`${TT_BASE}/rules/${encodeURIComponent(id)}`, payload).then(unwrap),
  deleteRule:  (id) =>
    client.delete(`${TT_BASE}/rules/${encodeURIComponent(id)}`).then(unwrap),

  // ── Supplier tax profiles ────────────────────────────────────
  // 1:1 against suppliers — carries PAN status, certificate metadata,
  // exemption flag. Upsert keyed on supplier_id.
  listProfiles:    (params = {}) =>
    client.get(`${TT_BASE}/supplier-profiles`, { params }).then(unwrap),
  getProfile:      (id) =>
    client.get(`${TT_BASE}/supplier-profiles/${encodeURIComponent(id)}`).then(unwrap),
  upsertProfile:   (id, payload = {}) =>
    client.put(`${TT_BASE}/supplier-profiles/${encodeURIComponent(id)}`, payload).then(unwrap),

  // ── Preview ──────────────────────────────────────────────────
  // Pre-flight calls that mirror the deduction the backend would
  // create on Purchase Bill / Supplier Payment post. Used by the
  // form to surface { applies, ratePercent, deductionAmount,
  // netPayableAmount, reason } before the operator commits.
  previewForPurchaseBill:    (purchaseBillId, body = {}) =>
    client.post(`${TT_BASE}/preview/purchase-bill/${encodeURIComponent(purchaseBillId)}`, body).then(unwrap),
  previewForSupplierPayment: (supplierPaymentId, body = {}) =>
    client.post(`${TT_BASE}/preview/supplier-payment/${encodeURIComponent(supplierPaymentId)}`, body).then(unwrap),

  // ── Deductions ───────────────────────────────────────────────
  // List + read are operator surfaces. Create is reserved for the
  // MANUAL source_type (e.g. journal-style adjustments). PURCHASE_BILL
  // / SUPPLIER_PAYMENT deductions are produced by the orchestrator
  // hooks; the manual endpoint is the only one exposed for direct
  // operator drafting.
  listDeductions:    (params = {}) =>
    client.get(`${TT_BASE}/deductions`, { params }).then(unwrap),
  getDeduction:      (id) =>
    client
      .get(`${TT_BASE}/deductions/${encodeURIComponent(id)}`)
      .then(unwrap),

  // Register-only reversal entry-point — invoked by the Purchase
  // Return orchestrator, mirrored here so operator tooling (e.g. a
  // future "Reverse" CTA on the deduction drawer) can call the same
  // shape directly when a return cycle has already been posted.
  reverseForPurchaseReturn: (purchaseReturnId, payload = {}) =>
    client.post(`${TT_BASE}/reverse/purchase-return/${encodeURIComponent(purchaseReturnId)}`, payload).then(unwrap),

  // ── Challans ─────────────────────────────────────────────────
  // A challan groups one or more deductions into a single
  // BSR / payment-reference deposit record. markPaid flips
  // tds_tcs_challans.status → PAID and rolls the linked deductions'
  // challan_status forward.
  listChallans:    (params = {}) =>
    client.get(`${TT_BASE}/challans`, { params }).then(unwrap),
  getChallan:      (id) =>
    client.get(`${TT_BASE}/challans/${encodeURIComponent(id)}`).then(unwrap),
  createChallan:   (payload = {}) =>
    client.post(`${TT_BASE}/challans`, payload).then(unwrap),
  markChallanPaid: (id, payload = {}) =>
    client
      .post(`${TT_BASE}/challans/${encodeURIComponent(id)}/mark-paid`, payload)
      .then(unwrap),

  // ── Reports ──────────────────────────────────────────────────
  // Three read-only roll-ups the Reports page renders side-by-side.
  // Filters: { from, to, financialYear, sectionCode, taxType,
  //           supplierId, status }.
  supplierSummary: (params = {}) =>
    client.get(`${TT_BASE}/reports/supplier-summary`, { params }).then(unwrap),
  sectionSummary:  (params = {}) =>
    client.get(`${TT_BASE}/reports/section-summary`, { params }).then(unwrap),
  pendingChallans: (params = {}) =>
    client.get(`${TT_BASE}/reports/pending-challans`, { params }).then(unwrap)
};

export default { tdsTcsApi };
